import Cookie from '@carvana/stc-cookie';
// import an experiment config file here when running an experiment, e.g.:
// import './car-stc-experiment-new-feature';
export * from './experiment-defaults';

// TypeScript interfaces
export interface ExperimentConfig {
  NAME: string;
  BUCKETS: string[];
  IDENTIFIER: string;
  BLOCKING?: boolean;
  NON_BUCKETED?: boolean;
  [key: string]: string | string[] | boolean | undefined;
}

// Type-safe experiment name - union of all valid experiment names
export type ExperimentName = keyof typeof EXPERIMENTS_REGISTRY;

export interface ExperimentMatch {
  experimentName: string;
  bucketValue: string;
  identifier: string;
  deleted?: boolean;
}

export interface AllExperimentsData {
  [experimentName: string]: {
    buckets: string[];
    identifier: string;
  };
}

// Centralized experiments registry
export const EXPERIMENTS_REGISTRY: Record<string, ExperimentConfig> = {
  // Add new experiments here when running them, e.g.:
  // 'new-feature': {
  //   NAME: 'New Feature Experiment',
  //   BUCKETS: ['control', 'variant1', 'variant2'],
  //   IDENTIFIER: 'new-feature-exp'
  // }
  // but preference is for a separate config file for each experiment
  // that imports this registry and adds itself to it, to keep things
  // modular and maintainable
};

/**
 * Get all experiments with their names, buckets, and identifiers
 */
export function getAllExperiments(): AllExperimentsData {
  const experiments: Record<string, { buckets: string[]; identifier: string }> = {};

  Object.entries(EXPERIMENTS_REGISTRY).forEach(([name, config]) => {
    experiments[name] = {
      buckets: config.BUCKETS,
      identifier: config.IDENTIFIER
    };
  });

  return experiments;
}

/**
 * Get a specific experiment configuration by name
 * @param name - The experiment name (must be a valid experiment name from EXPERIMENTS_REGISTRY)
 * @returns The experiment configuration
 * @throws Error in development if the experiment name is invalid
 */
export function getExperimentByName(name: ExperimentName): ExperimentConfig {
  const experiment = EXPERIMENTS_REGISTRY[name];

  if (!experiment) {
    const validNames = Object.keys(EXPERIMENTS_REGISTRY).join(', ');
    const errorMsg = `[getExperimentByName] Invalid experiment name: "${name}". Valid names are: ${validNames}`;

    if (process.env.NODE_ENV === 'development') {
      // Throw error in development to catch issues early
      throw new Error(errorMsg);
    } else {
      // Log error in production but don't crash the app
      // eslint-disable-next-line no-console
      console.error(errorMsg);
    }
  }

  return experiment;
}

/**
 * Check if any experiment matches the provided URL search parameters
 * @param urlSearchParams - URLSearchParams object from the current URL
 * @returns ExperimentMatch object if a match is found, null otherwise
 */
export function checkExperimentInQueryParams(
  urlSearchParams: URLSearchParams
): ExperimentMatch | null {
  const experimentEntries = Object.entries(EXPERIMENTS_REGISTRY);

  const foundMatch = experimentEntries.find(([experimentName, config]) => {
    const paramValue = urlSearchParams.get(experimentName);
    return paramValue && config.BUCKETS.includes(paramValue);
  });

  if (foundMatch) {
    const [experimentName, config] = foundMatch;
    const paramValue = urlSearchParams.get(experimentName);
    return {
      experimentName,
      bucketValue: paramValue!,
      identifier: config.IDENTIFIER
    };
  }

  return null;
}

/**
 * Set an experiment cookie with the given experiment name and bucket value
 * @param experimentName - The name of the experiment
 * @param bucketValue - The bucket value to set
 */
export function setExperimentCookie(experimentName: string, bucketValue: string): void {
  const cookieName = experimentName;
  const cookieValue = bucketValue;

  // Set cookie with 30 days expiration (30 days * 24 hours * 60 minutes = 43200 minutes)
  const expirationMinutes = 30 * 24 * 60;

  Cookie.setCookie(cookieName, cookieValue, expirationMinutes);
}

/**
 * Delete an experiment cookie
 * @param experimentName - The name of the experiment cookie to delete
 */
export function deleteExperimentCookie(experimentName: string): void {
  Cookie.deleteCookie(experimentName);
}

/**
 * Check URL parameters for experiments and set/delete cookies based on parameter values
 * This function should be called on app initialization or route changes
 *
 * Behavior:
 * - If param has a valid bucket value: Sets cookie and returns match
 * - If param exists but is empty (e.g., "experiment-name="): Deletes cookie and returns match with deleted flag
 * - If param doesn't exist or has invalid value: Returns null
 *
 * @param urlSearchParams - URLSearchParams object from the current URL
 * @returns Array of experiment matches that were processed (set or deleted)
 */
export function processExperimentQueryParams(urlSearchParams: URLSearchParams): ExperimentMatch[] {
  const experimentEntries = Object.entries(EXPERIMENTS_REGISTRY);

  return experimentEntries
    .map(([experimentName, config]): ExperimentMatch | null => {
      const paramValue = urlSearchParams.get(experimentName);

      // Check if parameter exists in URL
      if (paramValue !== null) {
        // Empty value means delete the cookie
        if (paramValue === '') {
          deleteExperimentCookie(experimentName);
          return {
            experimentName,
            bucketValue: '',
            identifier: config.IDENTIFIER,
            deleted: true
          };
        }

        // Non-empty value - check if it's a valid bucket
        if (config.BUCKETS.includes(paramValue)) {
          setExperimentCookie(experimentName, paramValue);
          return {
            experimentName,
            bucketValue: paramValue,
            identifier: config.IDENTIFIER
          };
        }
      }

      return null;
    })
    .filter((match): match is ExperimentMatch => match !== null);
}

/**
 * Get experiment names as an array
 */
export function getExperimentNames(): readonly string[] {
  return Object.keys(EXPERIMENTS_REGISTRY);
}

/**
 * Check if a given string is a valid experiment name
 */
export function isValidExperimentName(name: string): boolean {
  return name in EXPERIMENTS_REGISTRY;
}

/**
 * Get all possible bucket values across all experiments
 */
export function getAllPossibleBuckets(): readonly string[] {
  const allBuckets = new Set<string>();

  Object.values(EXPERIMENTS_REGISTRY).forEach(config => {
    config.BUCKETS.forEach(bucket => allBuckets.add(bucket));
  });

  return Array.from(allBuckets);
}
